/**
 * An application's Sandboxes v2 setting: `GET /org/applications/:id/sandbox`.
 * Delivery is either a connected GitHub repo or images pushed by the CLI.
 * The route answers `SANDBOX_V2_APPLICATION_NOT_SET_UP` (409) when the
 * application has no setting.
 *
 * @module hooks/useApplicationSandbox
 */
import { useQuery } from '@tanstack/react-query';
import { API_URL } from '../config';
import { toApiError } from '../lib/api-error';

/** An emulator operation; its input is a JSON Schema object (every property carries a `type`). */
export type SandboxV2Operation = {
  name: string;
  description: string;
  inputSchema: {
    properties?: Record<string, { type: string; description?: string; enum?: Array<string | number>; default?: unknown }>;
    required?: string[];
  };
  outputSchema: {
    properties?: Record<string, { type?: string; description?: string }>;
  };
};

/** A script action the definition declares (`actions:` in cofactor.sandbox.yaml). */
export type SandboxV2DeclaredAction = {
  slug: string;
  description: string;
  input: Record<
    string,
    { type: string; description?: string; required?: boolean; default?: string | number | boolean; enum?: Array<string | number>; sensitive?: boolean }
  >;
  output: Record<string, { type: string; description?: string }>;
};

export type ApplicationSandboxPush = {
  id: string;
  status: string;
  tags: string[];
  error: string | null;
  createdAt: string;
};

export type ApplicationSandbox = {
  applicationId: string;
  delivery: 'github' | 'upload';
  repo: string | null;
  definitionDir: string | null;
  sandboxEnvironments: Array<{ id: string; name: string; environment: string }>;
  /** Newest first. Null for GitHub delivery. */
  pushes: ApplicationSandboxPush[] | null;
  version: string | null;
  /** Null until the application has a ready version to read a definition from. */
  definition: { actions: SandboxV2DeclaredAction[]; operations: SandboxV2Operation[] } | null;
};

export async function fetchApplicationSandbox(applicationId: string): Promise<ApplicationSandbox> {
  const res = await fetch(`${API_URL}/org/applications/${applicationId}/sandbox`, {
    credentials: 'include',
  });
  if (!res.ok) throw await toApiError(res, 'Failed to load the application sandbox setting');
  const json = (await res.json()) as { data: ApplicationSandbox };
  return json.data;
}

/** Shared by every reader of `['application-sandbox', id]` so the key has one definition. */
export function applicationSandboxQueryOptions(applicationId: string) {
  return {
    queryKey: ['application-sandbox', applicationId] as const,
    queryFn: () => fetchApplicationSandbox(applicationId),
    staleTime: 60_000,
    // A not-set-up application answers with a user error; retrying it only delays the picker.
    retry: false,
  };
}

export function useApplicationSandbox(applicationId: string | null, enabled: boolean) {
  return useQuery({
    ...applicationSandboxQueryOptions(applicationId as string),
    enabled: enabled && !!applicationId,
  });
}
